import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { PlanAnnotation, PlanDragItem, PlanPoint } from "./planTypes";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ContourTarget {
  targetType: "unit" | "attribute";
  id: string;
  name: string;
  color: string;
}

interface PlanCanvasProps {
  imageDataUrl: string | null;
  mimeType: string;
  isPdf: boolean;
  page: number;
  onNumPages: (n: number) => void;
  annotations: PlanAnnotation[];
  labelFor: (a: PlanAnnotation) => string;
  colorFor: (a: PlanAnnotation) => string;
  /** Tenant name(s) for a placement, shown after the label on the plan; null when none/vacant. */
  tenantFor?: (a: PlanAnnotation) => string | null;
  /** Other units the placement's tenant also rents, shown under the tenant on the plan. */
  extraUnitsFor?: (a: PlanAnnotation) => string[];
  zoom: number;
  /** When set, the canvas is in contour-draw mode for this target. */
  contourTarget: ContourTarget | null;
  onContourFinish: () => void;
  onDropItem: (item: PlanDragItem, point: PlanPoint) => void;
  onMovePin: (annotation: PlanAnnotation, point: PlanPoint) => void;
  onSaveContour: (target: ContourTarget, points: PlanPoint[]) => void;
  onDeleteAnnotation: (annotation: PlanAnnotation) => void;
  highlightKey: string | null;
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));
const annKey = (a: PlanAnnotation) =>
  `${a.targetType}:${a.targetType === "unit" ? a.unitId : a.buildingAttribute?.id}`;

/**
 * Label card for a pin/contour on the plan: a white pill with a coloured status dot, the
 * unit/garage name in bold and — when rented — the tenant fully readable underneath. Names are
 * never truncated (no "…") so the tenant is always legible, per request.
 */
const PlanLabel: React.FC<{
  label: string;
  tenant?: string | null;
  /** Other units the tenant also rents (e.g. the apartment a garage tenant rents). */
  extraUnits?: string[];
  color: string;
  highlighted?: boolean;
}> = ({ label, tenant, extraUnits, color, highlighted }) => (
  <div
    className={`flex items-start gap-1.5 whitespace-nowrap rounded-md bg-white/95 px-2 py-1 shadow-sm ring-1 backdrop-blur-[1px] transition-shadow group-hover:bg-white group-hover:shadow-lg ${
      highlighted ? "ring-2 ring-neutral-primary" : "ring-black/10 group-hover:ring-neutral-primary/40"
    }`}
  >
    <span className="mt-[3px] h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
    <span className="flex flex-col leading-tight">
      <span className="text-[11px] font-semibold text-neutral-primary">{label}</span>
      {tenant && <span className="text-[10px] text-neutral-primary/80">{tenant}</span>}
      {extraUnits && extraUnits.length > 0 && (
        <span className="max-w-[220px] whitespace-normal break-words text-[10px] text-neutral-primary/55">
          ook huurder van: {extraUnits.join(", ")}
        </span>
      )}
    </span>
  </div>
);

export const PlanCanvas: React.FC<PlanCanvasProps> = ({
  imageDataUrl,
  isPdf,
  page,
  onNumPages,
  annotations,
  labelFor,
  colorFor,
  tenantFor,
  extraUnitsFor,
  zoom,
  contourTarget,
  onContourFinish,
  onDropItem,
  onMovePin,
  onSaveContour,
  onDeleteAnnotation,
  highlightKey,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [draftPoints, setDraftPoints] = useState<PlanPoint[]>([]);
  const [draggingPin, setDraggingPin] = useState<PlanAnnotation | null>(null);
  const [dragPreview, setDragPreview] = useState<{ id: string; x: number; y: number } | null>(null);

  // Track the scroll container width so we can size the (zoomable) stage and the PDF page.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset the in-progress contour whenever we leave draw mode or switch target.
  useEffect(() => {
    setDraftPoints([]);
  }, [contourTarget?.targetType, contourTarget?.id]);

  const stageWidth = Math.max(containerWidth * zoom, containerWidth);

  const pointFromEvent = useCallback((clientX: number, clientY: number): PlanPoint => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };
    const rect = stage.getBoundingClientRect();
    return {
      x: clamp(((clientX - rect.left) / rect.width) * 100),
      y: clamp(((clientY - rect.top) / rect.height) * 100),
    };
  }, []);

  // ── Drag a unit/attribute from the sidebar onto the plan → create a pin ──────────────
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    try {
      const item = JSON.parse(raw) as PlanDragItem;
      onDropItem(item, pointFromEvent(e.clientX, e.clientY));
    } catch {
      /* ignore malformed payload */
    }
  };

  // ── Click on the plan while drawing → add a contour point ────────────────────────────
  const handleStageClick = (e: React.MouseEvent) => {
    if (!contourTarget) return;
    setDraftPoints((prev) => [...prev, pointFromEvent(e.clientX, e.clientY)]);
  };

  const finishContour = () => {
    if (contourTarget && draftPoints.length >= 3) {
      onSaveContour(contourTarget, draftPoints);
    }
    setDraftPoints([]);
    onContourFinish();
  };

  const cancelContour = () => {
    setDraftPoints([]);
    onContourFinish();
  };

  // ── Reposition an existing pin (pointer drag) ────────────────────────────────────────
  useEffect(() => {
    if (!draggingPin) return;
    const handleMove = (e: PointerEvent) => {
      const point = pointFromEvent(e.clientX, e.clientY);
      setDragPreview({ id: annKey(draggingPin), ...point });
    };
    const handleUp = (e: PointerEvent) => {
      const point = pointFromEvent(e.clientX, e.clientY);
      onMovePin(draggingPin, point);
      setDraggingPin(null);
      setDragPreview(null);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [draggingPin, pointFromEvent, onMovePin]);

  const pins = annotations.filter((a) => a.shape === "pin");
  const contours = annotations.filter((a) => a.shape === "contour" && a.points && a.points.length >= 3);

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto rounded-lg border border-neutral-primary/15 bg-neutral-100"
      >
        {!imageDataUrl ? (
          <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
            Geen plattegrond geladen.
          </div>
        ) : (
          <div
            ref={stageRef}
            className={`relative mx-auto ${contourTarget ? "cursor-crosshair" : ""}`}
            style={{ width: stageWidth || "100%" }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
            }}
            onDrop={handleDrop}
            onClick={handleStageClick}
          >
            {/* Plan render */}
            {isPdf ? (
              <Document
                file={imageDataUrl}
                onLoadSuccess={({ numPages }) => onNumPages(numPages)}
                loading={<div className="p-8 text-sm text-neutral-secondary-mid">Plattegrond laden…</div>}
              >
                <Page
                  pageNumber={page}
                  width={stageWidth || containerWidth || undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            ) : (
              <img src={imageDataUrl} alt="Plattegrond" className="block w-full select-none" draggable={false} />
            )}

            {/* Contour overlay — viewBox in 0..100 % space, stretched to the stage so it tracks the plan */}
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {contours.map((c) => (
                <polygon
                  key={c.id}
                  points={(c.points as PlanPoint[]).map((p) => `${p.x},${p.y}`).join(" ")}
                  fill={colorFor(c)}
                  fillOpacity={highlightKey === annKey(c) ? 0.35 : 0.18}
                  stroke={colorFor(c)}
                  strokeWidth={highlightKey === annKey(c) ? 4 : 3}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {contourTarget && draftPoints.length > 0 && (
                <polyline
                  points={draftPoints.map((p) => `${p.x},${p.y}`).join(" ")}
                  fill="none"
                  stroke={contourTarget.color}
                  strokeWidth={3}
                  strokeDasharray="6,4"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </svg>

            {/* Draft contour vertices */}
            {contourTarget &&
              draftPoints.map((p, i) => (
                <span
                  key={i}
                  className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white"
                  style={{ left: `${p.x}%`, top: `${p.y}%`, backgroundColor: contourTarget.color }}
                />
              ))}

            {/* Contour delete handles (centroid) */}
            {contours.map((c) => {
              const pts = c.points as PlanPoint[];
              const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
              const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
              const tenant = tenantFor?.(c);
              return (
                <div
                  key={`label-${c.id}`}
                  className={`group absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 hover:z-50 ${
                    highlightKey === annKey(c) ? "z-40" : "z-10"
                  }`}
                  style={{ left: `${cx}%`, top: `${cy}%` }}
                >
                  <PlanLabel label={labelFor(c)} tenant={tenant} extraUnits={extraUnitsFor?.(c)} color={colorFor(c)} highlighted={highlightKey === annKey(c)} />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAnnotation(c);
                    }}
                    title="Contour verwijderen"
                    className="hidden h-4 w-4 items-center justify-center rounded-full bg-status-bad text-[10px] leading-none text-white group-hover:flex"
                  >
                    ×
                  </button>
                </div>
              );
            })}

            {/* Pins */}
            {pins.map((a) => {
              const preview = dragPreview && dragPreview.id === annKey(a) ? dragPreview : null;
              const x = preview ? preview.x : Number(a.x);
              const y = preview ? preview.y : Number(a.y);
              const highlighted = highlightKey === annKey(a);
              const tenant = tenantFor?.(a);
              return (
                <div
                  key={a.id}
                  className={`group absolute flex -translate-x-1/2 -translate-y-full flex-col items-center hover:z-50 ${
                    highlighted ? "z-40" : "z-20"
                  }`}
                  style={{ left: `${x}%`, top: `${y}%` }}
                  onPointerDown={(e) => {
                    if (contourTarget) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setDraggingPin(a);
                  }}
                >
                  <PlanLabel label={labelFor(a)} tenant={tenant} extraUnits={extraUnitsFor?.(a)} color={colorFor(a)} highlighted={highlighted} />
                  <span className="-mt-1 h-2 w-px bg-neutral-primary/30" />
                  <span
                    className="h-4 w-4 cursor-grab rounded-full border-2 border-white shadow active:cursor-grabbing"
                    style={{ backgroundColor: colorFor(a) }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAnnotation(a);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Pin verwijderen"
                    className="absolute -right-2 -top-2 hidden h-4 w-4 items-center justify-center rounded-full bg-status-bad text-[10px] leading-none text-white group-hover:flex"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Contour drawing toolbar */}
      {contourTarget && (
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-brick-primary/40 bg-brick-primary/10 px-3 py-2 text-sm">
          <span className="font-medium text-neutral-primary">
            Contour tekenen voor <span style={{ color: contourTarget.color }}>{contourTarget.name}</span> —
            klik punten op de plattegrond ({draftPoints.length})
          </span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => setDraftPoints((p) => p.slice(0, -1))}
              disabled={draftPoints.length === 0}
              className="rounded border border-neutral-primary/20 px-2 py-1 text-xs text-neutral-secondary-mid disabled:opacity-40"
            >
              Laatste punt wissen
            </button>
            <button
              type="button"
              onClick={cancelContour}
              className="rounded border border-neutral-primary/20 px-2 py-1 text-xs text-neutral-primary"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={finishContour}
              disabled={draftPoints.length < 3}
              className="rounded bg-brick-primary px-2 py-1 text-xs text-brick-secondary-fine disabled:opacity-40"
            >
              Afronden
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
